import React, { ReactElement } from 'react';
import { SCExternalLabel, SCHelperText, SCLabelWrapper, SCTextFieldWrapper } from './styles';
import { ITSTextFieldProps } from './types';
import { FormControl, Input, InputAdornment, InputLabel } from '@mui/material';

const TSTextField = ({
    children,
    id,
    ariaLabel,
    onChange,
    onBlur,
    onFocus,
    value,
    isDisabled,
    variant,
    placeholder,
    label,
    externalLabel,
    iconType,
    iconLocation,
    isRequired,
    type,
    size,
    helperText,
    maxLength,
    minLength,
    max,
    min,
    minRows,
    multiline,
    readOnly,
    startAdornment
}: ITSTextFieldProps): ReactElement => {
    // Wrap onChange to enforce maxLength for all input methods (including autofill/Faker)
    const handleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
        if (maxLength && event.target.value.length > maxLength) {
            event.target.value = event.target.value.substring(0, maxLength);
        }
        if (onChange) {
            onChange(event as React.ChangeEvent<HTMLInputElement>);
        }
    };
    const handlePaste = (event: React.ClipboardEvent<HTMLDivElement>): void => {
        if (maxLength) {
            const pastedText = event.clipboardData.getData('text');
            const currentValue = value || '';
            const input = event.target as HTMLInputElement;
            const selectionStart = input.selectionStart || 0;
            const selectionEnd = input.selectionEnd || 0;
            // Calculate what the new value would be after paste
            const newValue =
                currentValue.substring(0, selectionStart) + pastedText + currentValue.substring(selectionEnd);
            // If the new value exceeds maxLength, handle it manually
            if (newValue.length > maxLength) {
                event.preventDefault();
                const truncatedPaste = pastedText.substring(
                    0,
                    maxLength - (currentValue.length - (selectionEnd - selectionStart))
                );
                const truncatedValue =
                    currentValue.substring(0, selectionStart) + truncatedPaste + currentValue.substring(selectionEnd);
                // Create a synthetic event to trigger onChange
                const syntheticEvent = {
                    ...event,
                    target: {
                        ...input,
                        value: truncatedValue.substring(0, maxLength)
                    },
                    currentTarget: {
                        ...input,
                        value: truncatedValue.substring(0, maxLength)
                    }
                } as unknown as React.ChangeEvent<HTMLInputElement>;
                if (onChange) {
                    onChange(syntheticEvent);
                }
            }
        }
    };

    // console.info(
    //     '\n::::::::::::::::::::::::::::::::TSTextField:::::::::::::::::::::::::::::::::',
    //     '\n::id::',
    //     id,
    //     '\n::label::',
    //     label,
    //     '\n::placeholder::',
    //     placeholder,
    //     '\n::multiline::',
    //     multiline,
    //     '\n::value::',
    //     value,
    //     '\n:::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::'
    // );
    return (
        <SCTextFieldWrapper>
            {externalLabel && <SCExternalLabel htmlFor={id}>{externalLabel}</SCExternalLabel>}
            <FormControl variant={variant ? variant : 'standard'}>
                {!multiline && !externalLabel && <InputLabel htmlFor={id}>{label}</InputLabel>}
                {multiline && !externalLabel && (
                    <SCLabelWrapper>
                        {value && value.length > 0 && <InputLabel htmlFor={id}>{label}</InputLabel>}
                    </SCLabelWrapper>
                )}
                <Input
                    id={id}
                    value={value || ''}
                    size={size ? size : 'medium'}
                    onChange={handleChange}
                    onBlur={onBlur}
                    onFocus={onFocus}
                    onPaste={handlePaste}
                    placeholder={placeholder}
                    disabled={isDisabled}
                    readOnly={readOnly}
                    disableUnderline={readOnly}
                    required={isRequired}
                    type={type ? type : 'text'}
                    multiline={multiline ? multiline : false}
                    minRows={minRows ? minRows : 1}
                    inputProps={{
                        maxLength: maxLength,
                        minLength: minLength,
                        min: min,
                        max: max,
                        'aria-label': ariaLabel
                    }}
                    startAdornment={
                        (iconLocation && iconLocation === 'start' && iconType && (
                            <InputAdornment position="start">{iconType}</InputAdornment>
                        )) ||
                        startAdornment
                    }
                    endAdornment={
                        iconLocation &&
                        iconLocation === 'end' &&
                        iconType && <InputAdornment position="start">{iconType}</InputAdornment>
                    }
                />
            </FormControl>
            {helperText && <SCHelperText>{helperText}</SCHelperText>}
        </SCTextFieldWrapper>
    );
};

export { TSTextField };
